import React, { useMemo, useState } from "react";
import BUSINESS from "../config/business";

const SERVICE_OPTIONS = [
  "Doors and windows",
  "Fences and gates",
  "Decks and porches",
  "Sheds and outbuildings",
  "Finish woodworking",
  "Painting",
  "Core drilling",
  "Concrete slabs",
  "Small remodel",
  "Several smaller projects",
  "Other / let's discuss",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    town: "",
    reply: "",
    service: "",
    timing: "",
    description: "",
  });
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState("");

  const isLive = useMemo(() => {
    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(BUSINESS.email || "");
    return Boolean(BUSINESS.live && validEmail);
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const v = (k) => String(form[k] || "").trim();
    const subject = `Project inquiry: ${v("service")} in ${v("town")}`;
    const body = [
      "Hello Jason,",
      "",
      `Name: ${v("name")}`,
      `Reply contact: ${v("reply")}`,
      `Project town: ${v("town")}`,
      `Project type: ${v("service")}`,
      `Timing and available workdays: ${v("timing") || "To discuss"}`,
      "",
      "Project details:",
      v("description"),
      "",
      "Photos can be attached to this email before sending.",
    ].join("\n");

    setPreview(`Subject: ${subject}\n\n${body}`);

    if (!isLive) {
      setMessage(
        "Preview only. Nothing has been sent or saved. Here is the project request that could be emailed after business contact details are connected."
      );
      return;
    }
    setMessage(
      `Your email app should open with this request. Review it, attach any photos, and press Send there. This website has not submitted or booked your project. If no app opens, copy the request below and email it to ${BUSINESS.email}.`
    );
    window.location.href =
      `mailto:${BUSINESS.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <section className="section contact" id="contact" aria-labelledby="contact-title">
      <div className="wrap">
        <div className="contact-box">
          <div className="contact-info">
            <span className="eyebrow">Let's start with your list</span>
            <h2 id="contact-title">What would you<br />like to get done?</h2>
            <p>Tell us a little about the project and where it is located. No polished plans needed for an initial conversation.</p>
            <p>Helpful details: the town, approximate dimensions, desired materials, timing, and whether you have photos available.</p>

            <div className="direct" id="direct-contact">
              {isLive ? (
                <>
                  <strong>Contact Jason</strong>
                  <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a>
                  {BUSINESS.phone && /^\+?\d{10,15}$/.test(BUSINESS.phone.replace(/[^\d+]/g, "")) && (
                    <a href={`tel:${BUSINESS.phone.replace(/[^\d+]/g, "")}`}>
                      {BUSINESS.phoneDisplay || BUSINESS.phone}
                    </a>
                  )}
                  {BUSINESS.googleReviewUrl && (
                    <a
                      className="google-review-link"
                      href={BUSINESS.googleReviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span aria-hidden="true">&#9733;</span> Review us on Google
                    </a>
                  )}
                </>
              ) : (
                <>
                  <strong>Contact details pending</strong>
                  <p>A dedicated business phone and email will be added before launch.</p>
                </>
              )}
            </div>

            {!isLive && (
              <p className="draft-note">
                Website preview only. This draft does not send, save, or book a project request. Use the form to preview the inquiry experience.
              </p>
            )}
          </div>

          <form className="form" id="project-form" onSubmit={handleSubmit} noValidate>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="name">Your name</label>
                <input id="name" name="name" autoComplete="name" required maxLength={100} value={form.name} onChange={onChange} />
              </div>
              <div className="field">
                <label htmlFor="town">Project town</label>
                <input id="town" name="town" autoComplete="address-level2" required maxLength={100} value={form.town} onChange={onChange} />
              </div>
              <div className="field">
                <label htmlFor="reply">Email or phone for a reply</label>
                <input id="reply" name="reply" required maxLength={160} value={form.reply} onChange={onChange} />
              </div>
              <div className="field">
                <label htmlFor="service">Type of project</label>
                <select id="service" name="service" required value={form.service} onChange={onChange}>
                  <option value="">Please select</option>
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </div>
              <div className="field full">
                <label htmlFor="timing">Preferred timeframe and available workdays</label>
                <input
                  id="timing"
                  name="timing"
                  placeholder="For example: this fall; flexible on weekdays"
                  maxLength={180}
                  value={form.timing}
                  onChange={onChange}
                />
              </div>
              <div className="field full">
                <label htmlFor="description">Tell us about the work</label>
                <textarea
                  id="description"
                  name="description"
                  required
                  maxLength={3000}
                  placeholder="What needs attention? Approximate size? Photos available?"
                  value={form.description}
                  onChange={onChange}
                />
              </div>
            </div>
            <p className="form-note">
              Do not include access codes, payment details, or other sensitive information. For a live email inquiry, photos can be attached in your email app before sending.
            </p>
            <button type="submit" className="btn" id="request-button">
              {isLive ? "Open email request" : "Preview project request"} <span aria-hidden="true">&rarr;</span>
            </button>
            <p className="form-message" id="form-message" role="status" aria-live="polite">{message}</p>
            {preview && <div className="request-preview" id="request-preview">{preview}</div>}
          </form>
        </div>
      </div>
    </section>
  );
}
